import React, { useCallback } from 'react';

import { Box, HStack, Typography } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { useStoreContext } from 'features/store';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import { logAddRecentItemToCartEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { routes } from 'utils/routing';

import { MinimalRecentOrderCard } from './minimal-recent-order-card';
import { MinimalRecentOrderFavoriteButton } from './minimal-recent-order-favorite-button';
import { MinimalRecentOrderData } from './types';
import { useMinimalRecentEntryAvailability } from './use-minimal-recent-entry-availability';

const Header = HStack.withConfig({
  paddingTop: '$1',
  paddingBottom: '$4',
  paddingX: '$3',
  alignItems: 'center',
  justifyContent: 'space-between',
});

interface MinimalRecentOrderCardGroupProps {
  order: MinimalRecentOrderData;
}

const MinimalRecentOrderCardGroup: React.FC<MinimalRecentOrderCardGroupProps> = ({ order }) => {
  const { replace } = useRouter();
  const { store } = useStoreContext();
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const {
    availability,
    checkingAvailability,
    hasSomeAvailableEntry,
  } = useMinimalRecentEntryAvailability(order.minimalEntries);
  const orderNumber = order.orderNumberFormatted;
  const formattedDate = new Date(order.createdAt).toLocaleString(t('localeDateTime'), {
    month: 'long',
    day: 'numeric',
  });

  const handleReorder = useCallback(() => {
    const availableEntries = order.minimalEntries.filter(
      (_, index) => availability[index]?.itemAvailabilityStatus === ItemAvailabilityStatus.AVAILABLE
    );

    availableEntries.forEach(entry => {
      logAddRecentItemToCartEvent(entry?.name ?? '', store);
      addMappedEntryToCart(entry);
    });

    const [firstEntry] = availableEntries;
    if (firstEntry) {
      const message =
        availableEntries.length === 1
          ? firstEntry.quantity > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: firstEntry.quantity - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name })
          : t('cartUpdatedRecentReorder', 'Your cart was updated');

      toaster.addToCartToast(message, firstEntry.image);
    }

    replace(routes.cart);
  }, [addMappedEntryToCart, availability, order.minimalEntries, replace, store]);

  if (!order.minimalEntries.length) {
    return null;
  }

  return (
    <Box testID="recent-order-card-group" paddingBottom="$3">
      <Header>
        <HStack alignItems="center">
          <Typography fontSize="sm" bold>
            {formattedDate}
          </Typography>
          <MinimalRecentOrderFavoriteButton order={order} />
          {orderNumber && (
            <Typography fontSize="sm" ml="$2">
              {t('order')} #{orderNumber}
            </Typography>
          )}
        </HStack>
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!hasSomeAvailableEntry}
          size={ActionButtonSizes.SMALL}
          aria-label={
            orderNumber ? t('reorderWithOrderNumber', { ticketNumber: orderNumber }) : t('reorder')
          }
        >
          {t('reorder')}
        </ActionButton>
      </Header>
      {order.minimalEntries.map((entry, index) => (
        <MinimalRecentOrderCard
          key={entry.cartId ?? `${entry.id}-${index}`}
          entry={entry}
          availability={
            availability[index] ?? {
              itemAvailabilityStatus: ItemAvailabilityStatus.UNAVAILABLE,
              dayParts: [],
            }
          }
          checkingAvailability={checkingAvailability}
        />
      ))}
    </Box>
  );
};

export default MinimalRecentOrderCardGroup;
